/**
 * GET /internal/quotation/catalog — sales-floor only, never procurement.
 * Listed prices are 挂牌价 (official model price × platform SKU rate_multiplier).
 * `*_base` fields are the unmultiplied vendor list price.
 */
import type { BrandId } from './brands.ts';
import type { BilingualLabel } from './metering-presentation.ts';
import type { UnitUsageUnit } from './metering.ts';
import type { CatalogBillingPromptTierBasis } from './public-catalog.ts';

/** USD per Mtok, already × rate_multiplier. `null` means the dimension is not priced. */
export interface QuotationTokenPrices {
  readonly input: number | null;
  readonly output: number | null;
  readonly cache_read: number | null;
  readonly cache_write: number | null;
  readonly cache_write_5m: number | null;
  readonly cache_write_1h: number | null;
  readonly reasoning: number | null;
  readonly image_input: number | null;
}

export interface QuotationPromptLengthTier {
  readonly min_prompt_tokens: number;
  readonly rates: QuotationTokenPrices;
  /** vendor list price before the platform multiplier; 挂牌价 = base × rate_multiplier */
  readonly rates_base: QuotationTokenPrices;
}

export interface QuotationUnitPrice {
  readonly kind: 'flat' | 'tier' | 'surcharge';
  readonly unit: UnitUsageUnit;
  readonly unit_label: BilingualLabel;
  /** Tier cell axis values, `{variant_key}` for a surcharge, or `null` for a flat rate. */
  readonly variant: Readonly<Record<string, string>> | null;
  /** Human text from axis labels (zh), e.g. "分辨率=1080p, 含视频输入". `null` for flat. */
  readonly variant_label: string | null;
  /** `(micros / 1e6) × rate_multiplier`. */
  readonly usd_per_unit: number;
  /** vendor list price before the platform multiplier; 挂牌价 = base × rate_multiplier */
  readonly base_usd_per_unit: number;
}

export interface QuotationTokenTierRow {
  readonly variant: Readonly<Record<string, string>>;
  readonly variant_label: string;
  readonly rates: QuotationTokenPrices | null;
  /** vendor list price before the platform multiplier; 挂牌价 = base × rate_multiplier */
  readonly rates_base: QuotationTokenPrices | null;
  readonly single_rate_per_mtok: number | null;
  /** vendor list price before the platform multiplier; 挂牌价 = base × rate_multiplier */
  readonly single_rate_per_mtok_base: number | null;
}

export type QuotationCategory = 'text' | 'image' | 'video';
export type QuotationCurrency = 'CNY' | 'USD';
export const QUOTATION_CURRENCIES: readonly QuotationCurrency[] = ['CNY', 'USD'];
export const QUOTATION_DRAFT_VERSION = 2;

export interface QuotationSku {
  readonly sku_id: string;
  readonly sku_name: string;
  readonly namespace_id: string;
  readonly namespace_slug: string;
  readonly vendor: string;
  readonly model: string;
  readonly model_display_name: string | null;
  /** Grouping used by the exported workbook; derived from the metering profile kind. */
  readonly category: QuotationCategory;
  /** `CatalogVendor.display_name`; falls back to the vendor slug when the catalog has no row. */
  readonly vendor_display_name: string;
  /** Platform multiplier that was applied to every price on this SKU. */
  readonly rate_multiplier: number;
  readonly billing_mode: 'token' | 'unit';
  /** Token-mode base card from the flat CatalogSku fields. `null` in unit mode. */
  readonly token_prices: QuotationTokenPrices | null;
  /** vendor list price before the platform multiplier; 挂牌价 = base × rate_multiplier */
  readonly token_prices_base: QuotationTokenPrices | null;
  /** From `billing.prompt_length_tiers`; `null` when the catalog omits the ladder. */
  readonly prompt_length_tiers: readonly QuotationPromptLengthTier[] | null;
  readonly prompt_length_basis: CatalogBillingPromptTierBasis | null;
  /** Token-mode `billing.tiers` rows (matrix). `null` when there is no token matrix. */
  readonly token_tier_rows: readonly QuotationTokenTierRow[] | null;
  /**
   * Unit mode: flat `unit_rate` + unit-rate tier rows + surcharges.
   * Token mode: surcharges only. `null` when there are none — never `[]`.
   */
  readonly unit_prices: readonly QuotationUnitPrice[] | null;
  readonly sales_floor_multiplier: number | null;
}

export interface QuotationNamespace {
  readonly namespace_id: string;
  readonly namespace_slug: string;
  readonly namespace_display_name: string;
  readonly sales_floor_multiplier: number | null;
  readonly has_unconfigured_sales_cost: boolean;
  readonly skus: readonly QuotationSku[];
}

export interface QuotationCatalogResponse {
  readonly brand: BrandId;
  readonly generated_at: string;
  readonly fx_rate: number;
  readonly namespaces: readonly QuotationNamespace[];
}

/** Per-SKU discount override; blank `discount_zhe` means "inherit the namespace 折". */
export interface QuotationDraftSku {
  readonly discount_zhe: string;
}

export interface QuotationDraftNamespace {
  readonly included: boolean;
  readonly discount_zhe: string;
  /** Keyed by `sku_id`. `{}` when nothing is overridden. */
  readonly skus: Readonly<Record<string, QuotationDraftSku>>;
}

/** The pre-2026-10-10 payload; still accepted on write and upgraded on read. */
export interface QuotationDraftPayloadV1 {
  readonly version: 1;
  readonly customer_name: string;
  readonly quote_date: string;
  readonly valid_days: string;
  readonly note: string;
  readonly namespaces: Readonly<
    Record<string, { readonly included: boolean; readonly discount_zhe: string }>
  >;
}

/** Versioned body stored in `crm_quotations.draft_json`. Unknown keys are dropped on write. */
export interface QuotationDraftPayload {
  readonly version: 2;
  readonly currency: QuotationCurrency;
  readonly customer_name: string;
  readonly quote_date: string;
  readonly valid_days: string;
  readonly note: string;
  readonly namespaces: Readonly<Record<string, QuotationDraftNamespace>>;
}

/** List row: header only, no draft body. */
export interface CrmQuotationSummary {
  readonly id: string;
  readonly owner_ops_user_id: string;
  readonly brand: BrandId;
  readonly customer_name: string;
  readonly quote_date: string;
  readonly export_count: number;
  readonly last_exported_at: string;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface CrmQuotationRecord extends CrmQuotationSummary {
  readonly draft: QuotationDraftPayload;
}

export interface ListCrmQuotationsResponse {
  readonly quotations: readonly CrmQuotationSummary[];
}

export interface GetCrmQuotationResponse {
  readonly quotation: CrmQuotationRecord;
}

export interface UpsertCrmQuotationRequest {
  readonly owner_ops_user_id: string;
  readonly brand: BrandId;
  readonly draft: QuotationDraftPayloadV1 | QuotationDraftPayload;
}

export interface UpsertCrmQuotationResponse {
  readonly quotation: CrmQuotationRecord;
  readonly created: boolean;
}

export const CRM_QUOTATION_CUSTOMER_NAME_MAX = 200;

/**
 * Uniqueness key for one customer under a sales owner.
 * "ACME", "acme " and "Acme  Corp" collapse to the same key.
 */
export function crmQuotationCustomerKey(name: string): string {
  return name.trim().replace(/\s+/gu, ' ').toLowerCase();
}

/** nadoutong quotes in CNY, every other brand in USD. */
export function quotationDefaultCurrency(brand: BrandId): QuotationCurrency {
  return brand === 'nadoutong' ? 'CNY' : 'USD';
}

/** v1 → v2 (currency from the brand, `skus: {}` on every namespace); v2 is returned unchanged. */
export function upgradeQuotationDraft(
  draft: QuotationDraftPayloadV1 | QuotationDraftPayload,
  brand: BrandId,
): QuotationDraftPayload {
  if (draft.version === 2) return draft;
  const namespaces: Record<string, QuotationDraftNamespace> = {};
  for (const [key, namespace] of Object.entries(draft.namespaces)) {
    namespaces[key] = {
      included: namespace.included,
      discount_zhe: namespace.discount_zhe,
      skus: {},
    };
  }
  return {
    version: QUOTATION_DRAFT_VERSION,
    currency: quotationDefaultCurrency(brand),
    customer_name: draft.customer_name,
    quote_date: draft.quote_date,
    valid_days: draft.valid_days,
    note: draft.note,
    namespaces,
  };
}

export function isQuotationCurrency(value: unknown): value is QuotationCurrency {
  return value === 'CNY' || value === 'USD';
}
